import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { crewService, jobService } from "../services/database";
import { useAuth } from "../contexts/AuthContext";
import type { CrewMember } from "../types/database";
import { format } from "date-fns";
import Modal from "../components/Modal";
import CrewDocumentForm from "../components/CrewDocumentForm";

type TabType = "profile" | "payment" | "documents" | "availability" | "history";

// Job History List Component
function JobHistoryList({
  jobIds,
  organizationId,
}: {
  jobIds: string[];
  organizationId: string;
}) {
  const navigate = useNavigate();

  // Fetch all jobs for the organization
  const {
    data: allJobs = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["jobs", organizationId],
    queryFn: () => jobService.getJobs(organizationId),
    enabled: !!organizationId,
  });

  // Filter jobs that are in the crew member's history
  const jobHistory = allJobs.filter((job) => jobIds.includes(job.id));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        Error loading job history. Please try again.
      </div>
    );
  }

  if (jobHistory.length === 0) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-500">No jobs found in history.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {jobHistory.map((job) => (
        <div
          key={job.id}
          className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => navigate(`/org/${organizationId}/jobs/${job.id}`)}
        >
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {job.title}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Client:</span>
                  <p className="text-gray-900">{job.client}</p>
                </div>
                <div>
                  <span className="text-gray-500">Dates:</span>
                  <p className="text-gray-900">
                    {format(new Date(job.start_date), "dd MMM yyyy")} -{" "}
                    {format(new Date(job.end_date), "dd MMM yyyy")}
                  </p>
                </div>
              </div>
              <div className="mt-2">
                <span className="text-gray-500 text-sm">Status:</span>
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ml-2 ${
                    job.status === "completed"
                      ? "bg-green-100 text-green-800"
                      : job.status === "in_progress"
                      ? "bg-blue-100 text-blue-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {job.status.replace("_", " ").charAt(0).toUpperCase() +
                    job.status.slice(1).replace("_", " ")}
                </span>
              </div>
            </div>
            <div className="ml-4">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Helper function to get the appropriate icon for document type
function getDocumentIcon(type: string) {
  switch (type.toLowerCase()) {
    case "identification":
      return (
        <svg
          className="h-8 w-8 text-blue-400 mr-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2"
          />
        </svg>
      );
    case "certification":
      return (
        <svg
          className="h-8 w-8 text-green-400 mr-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
          />
        </svg>
      );
    case "contract":
      return (
        <svg
          className="h-8 w-8 text-purple-400 mr-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      );
    case "insurance":
      return (
        <svg
          className="h-8 w-8 text-red-400 mr-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
          />
        </svg>
      );
    default:
      return (
        <svg
          className="h-8 w-8 text-gray-400 mr-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      );
  }
}

export default function CrewDetail() {
  const { orgId, id: crewId } = useParams(); // Changed from 'crewId' to 'id' to match the route param
  const navigate = useNavigate();
  const { currentOrganization } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>("profile");
  const [isAddDocumentModalOpen, setIsAddDocumentModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<{
    id: string;
    name: string;
    url: string;
    type: string;
    uploaded_at: string;
  } | null>(null);

  // Redirect to crew list if no ID is provided
  useEffect(() => {
    if (!crewId && orgId) {
      console.log("No crew ID provided, redirecting to crew list");
      navigate(`/org/${orgId}/crew`);
    }
  }, [crewId, orgId, navigate]);

  // Fetch crew member data
  const {
    data: crewMember,
    isLoading,
    error,
    refetch,
  } = useQuery<CrewMember>({
    queryKey: ["crew", crewId],
    queryFn: async () => {
      try {
        console.log("Fetching crew member with ID:", crewId);
        console.log("Organization ID:", currentOrganization?.id);

        if (!crewId) {
          throw new Error("Crew member ID is missing");
        }

        if (!currentOrganization?.id) {
          throw new Error("Organization ID is missing");
        }

        const result = await crewService.getCrewMember(
          crewId,
          currentOrganization.id
        );
        console.log("Fetch result:", result);
        return result;
      } catch (err) {
        console.error("Error fetching crew member:", err);
        throw err;
      }
    },
    enabled: !!crewId && !!currentOrganization?.id,
    retry: 1, // Only retry once to avoid excessive requests on permanent errors
  });

  // Format currency
  const formatCurrency = (amount: number | null, currency: string) => {
    if (amount === null) return "N/A";

    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency || "GBP",
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "dd MMM yyyy");
  };

  // If no crew ID, show a message and redirect
  if (!crewId) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="text-gray-500 mb-2 text-lg font-medium">
          No crew member selected
        </div>
        <div className="text-sm text-gray-500 mb-4">
          Redirecting to crew list...
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !crewMember) {
    const errorDetails =
      error instanceof Error ? error.message : JSON.stringify(error, null, 2);

    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="text-red-500 mb-2 text-lg font-medium">
          Error loading crew member details
        </div>
        <div className="text-sm text-gray-500 mb-4 max-w-2xl overflow-auto">
          {errorDetails === "null" ? (
            <p>
              Unable to retrieve crew member data. This may be due to missing
              permissions or the crew member doesn't exist.
            </p>
          ) : (
            <pre className="whitespace-pre-wrap bg-gray-100 p-4 rounded-md">
              {errorDetails}
            </pre>
          )}
        </div>
        <div className="flex space-x-4">
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
          >
            Try Again
          </button>
          <button
            onClick={() => navigate(`/org/${orgId}/crew`)}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700"
          >
            Return to Crew List
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header with back button */}
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(`/org/${orgId}/crew`)}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <svg
              className="h-5 w-5 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {crewMember.first_name} {crewMember.last_name}
            </h1>
            <p className="text-sm text-gray-500">{crewMember.role_type}</p>
          </div>
          <div className="ml-auto flex space-x-2">
            <Link
              to={`/org/${orgId}/crew/${crewId}/edit`}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              Edit Profile
            </Link>
          </div>
        </div>

        {/* Profile header card */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="md:flex">
            {/* Profile image */}
            <div className="md:w-1/3 bg-gray-50">
              <div className="aspect-square">
                {crewMember.profile_image ? (
                  <img
                    src={crewMember.profile_image}
                    alt={`${crewMember.first_name} ${crewMember.last_name}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-300">
                    <span className="text-6xl font-bold text-gray-500">
                      {crewMember.first_name.charAt(0)}
                      {crewMember.last_name.charAt(0)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Profile summary */}
            <div className="p-6 md:w-2/3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Contact info */}
                <div>
                  <h3 className="text-sm font-medium uppercase text-gray-500 mb-3">
                    Contact Information
                  </h3>
                  <ul className="space-y-2">
                    {crewMember.email && (
                      <li className="flex items-start">
                        <svg
                          className="h-5 w-5 text-gray-400 mr-2 mt-0.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                        <span className="text-gray-900">
                          {crewMember.email}
                        </span>
                      </li>
                    )}
                    {crewMember.phone && (
                      <li className="flex items-start">
                        <svg
                          className="h-5 w-5 text-gray-400 mr-2 mt-0.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                        <span className="text-gray-900">
                          {crewMember.phone}
                        </span>
                      </li>
                    )}
                    {crewMember.address && (
                      <li className="flex items-start">
                        <svg
                          className="h-5 w-5 text-gray-400 mr-2 mt-0.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        <div>
                          {crewMember.address.street && (
                            <div className="text-gray-900">
                              {crewMember.address.street}
                            </div>
                          )}
                          {(crewMember.address.city ||
                            crewMember.address.state ||
                            crewMember.address.postal_code) && (
                            <div className="text-gray-900">
                              {[
                                crewMember.address.city,
                                crewMember.address.state,
                                crewMember.address.postal_code,
                              ]
                                .filter(Boolean)
                                .join(", ")}
                            </div>
                          )}
                          {crewMember.address.country && (
                            <div className="text-gray-900">
                              {crewMember.address.country}
                            </div>
                          )}
                        </div>
                      </li>
                    )}
                  </ul>
                </div>

                {/* Rates */}
                <div>
                  <h3 className="text-sm font-medium uppercase text-gray-500 mb-3">
                    Rates
                  </h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    {crewMember.day_rate !== null && (
                      <div className="mb-2">
                        <span className="text-sm text-gray-500">Day Rate:</span>
                        <div className="text-xl font-medium text-gray-900">
                          {formatCurrency(
                            crewMember.day_rate,
                            crewMember.currency
                          )}
                        </div>
                      </div>
                    )}
                    {crewMember.hourly_rate !== null && (
                      <div>
                        <span className="text-sm text-gray-500">
                          Hourly Rate:
                        </span>
                        <div className="text-lg font-medium text-gray-900">
                          {formatCurrency(
                            crewMember.hourly_rate,
                            crewMember.currency
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Specialties */}
                  <div className="mt-4">
                    <h3 className="text-sm font-medium uppercase text-gray-500 mb-2">
                      Specialties
                    </h3>
                    <div className="flex flex-wrap gap-1">
                      {crewMember.specialties.map((specialty, index) => (
                        <span
                          key={index}
                          className="inline-block bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Tags */}
              {crewMember.tags.length > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <h3 className="text-sm font-medium uppercase text-gray-500 mb-2">
                    Tags
                  </h3>
                  <div className="flex flex-wrap gap-1">
                    {crewMember.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-block bg-primary/10 text-primary text-xs px-2 py-1 rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab("profile")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "profile"
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Profile
            </button>
            <button
              onClick={() => setActiveTab("payment")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "payment"
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Payment Details
            </button>
            <button
              onClick={() => setActiveTab("documents")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "documents"
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Documents
            </button>
            <button
              onClick={() => setActiveTab("availability")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "availability"
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Availability
            </button>
            <button
              onClick={() => setActiveTab("history")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "history"
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Job History
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          {/* Profile Tab */}
          {activeTab === "profile" && (
            <div className="p-6 space-y-6">
              {/* Notes */}
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-4">
                  Notes
                </h2>
                <div className="bg-gray-50 p-4 rounded-lg">
                  {crewMember.notes ? (
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {crewMember.notes}
                    </p>
                  ) : (
                    <p className="text-gray-500 italic">
                      No notes available for this crew member.
                    </p>
                  )}
                </div>
              </div>

              {/* Emergency Contact */}
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-4">
                  Emergency Contact
                </h2>
                {crewMember.emergency_contact &&
                (crewMember.emergency_contact.name ||
                  crewMember.emergency_contact.phone) ? (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    {crewMember.emergency_contact.name && (
                      <div className="mb-2">
                        <span className="text-sm font-medium text-gray-500">
                          Name:
                        </span>
                        <p className="text-gray-900">
                          {crewMember.emergency_contact.name}
                        </p>
                      </div>
                    )}
                    {crewMember.emergency_contact.relationship && (
                      <div className="mb-2">
                        <span className="text-sm font-medium text-gray-500">
                          Relationship:
                        </span>
                        <p className="text-gray-900">
                          {crewMember.emergency_contact.relationship}
                        </p>
                      </div>
                    )}
                    {crewMember.emergency_contact.phone && (
                      <div>
                        <span className="text-sm font-medium text-gray-500">
                          Phone:
                        </span>
                        <p className="text-gray-900">
                          {crewMember.emergency_contact.phone}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500 italic">
                    No emergency contact information available.
                  </p>
                )}
              </div>

              {/* Record info */}
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-4">
                  Record Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Created:
                    </span>
                    <p className="text-gray-900">
                      {formatDate(crewMember.created_at)}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Last Updated:
                    </span>
                    <p className="text-gray-900">
                      {formatDate(crewMember.updated_at)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Payment Details Tab */}
          {activeTab === "payment" && (
            <div className="p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Payment Details
              </h2>
              {crewMember.payment_details &&
              Object.keys(crewMember.payment_details).length > 0 ? (
                <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Payment Method:
                    </span>
                    <p className="text-gray-900">
                      {crewMember.payment_details.payment_method}
                    </p>
                  </div>

                  {crewMember.payment_details.payment_method ===
                    "Bank Transfer" && (
                    <>
                      {crewMember.payment_details.account_name && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">
                            Account Name:
                          </span>
                          <p className="text-gray-900">
                            {crewMember.payment_details.account_name}
                          </p>
                        </div>
                      )}
                      {crewMember.payment_details.account_number && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">
                            Account Number:
                          </span>
                          <p className="text-gray-900">
                            {crewMember.payment_details.account_number}
                          </p>
                        </div>
                      )}
                      {crewMember.payment_details.sort_code && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">
                            Sort Code:
                          </span>
                          <p className="text-gray-900">
                            {crewMember.payment_details.sort_code}
                          </p>
                        </div>
                      )}
                      {crewMember.payment_details.bank_name && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">
                            Bank Name:
                          </span>
                          <p className="text-gray-900">
                            {crewMember.payment_details.bank_name}
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {crewMember.payment_details.payment_method === "PayPal" &&
                    crewMember.payment_details.paypal_email && (
                      <div>
                        <span className="text-sm font-medium text-gray-500">
                          PayPal Email:
                        </span>
                        <p className="text-gray-900">
                          {crewMember.payment_details.paypal_email}
                        </p>
                      </div>
                    )}

                  {crewMember.payment_details.other_details && (
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Other Details:
                      </span>
                      <p className="text-gray-900 whitespace-pre-wrap">
                        {crewMember.payment_details.other_details}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 italic">
                  No payment details available.
                </p>
              )}
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === "documents" && (
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-medium text-gray-900">Documents</h2>
                <button
                  onClick={() => {
                    setEditingDocument(null);
                    setIsAddDocumentModalOpen(true);
                  }}
                  className="px-3 py-1 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm flex items-center"
                >
                  <svg
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Add Document
                </button>
              </div>

              {crewMember.documents && crewMember.documents.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {crewMember.documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                    >
                      <div className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center">
                            {getDocumentIcon(doc.type)}
                            <div>
                              <h3 className="text-sm font-medium text-gray-900">
                                {doc.name}
                              </h3>
                              <p className="text-xs text-gray-500 capitalize">
                                {doc.type}
                              </p>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => {
                                setEditingDocument(doc);
                                setIsAddDocumentModalOpen(true);
                              }}
                              className="text-gray-400 hover:text-primary"
                              title="Edit document"
                            >
                              <svg
                                className="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                />
                              </svg>
                            </button>
                            <a
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline text-sm flex items-center"
                            >
                              <svg
                                className="h-4 w-4 mr-1"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                />
                              </svg>
                              View
                            </a>
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                          Uploaded: {formatDate(doc.uploaded_at)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <svg
                    className="mx-auto h-16 w-16 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <p className="mt-4 text-sm text-gray-500">
                    No documents available for this crew member.
                  </p>
                  <button
                    onClick={() => {
                      setEditingDocument(null);
                      setIsAddDocumentModalOpen(true);
                    }}
                    className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm inline-flex items-center"
                  >
                    <svg
                      className="h-4 w-4 mr-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Upload Document
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Availability Tab */}
          {activeTab === "availability" && (
            <div className="p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Availability
              </h2>
              {crewMember.availability &&
              crewMember.availability.general_availability ? (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">
                      General Availability
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {crewMember.availability.general_availability}
                      </p>
                    </div>
                  </div>

                  {crewMember.availability.unavailable_dates &&
                    crewMember.availability.unavailable_dates.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-2">
                          Unavailable Dates
                        </h3>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <ul className="space-y-1">
                            {crewMember.availability.unavailable_dates.map(
                              (date, index) => (
                                <li key={index} className="text-gray-700">
                                  {date}
                                </li>
                              )
                            )}
                          </ul>
                        </div>
                      </div>
                    )}
                </div>
              ) : (
                <p className="text-gray-500 italic">
                  No availability information recorded.
                </p>
              )}
            </div>
          )}

          {/* Job History Tab */}
          {activeTab === "history" && (
            <div className="p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Job History
              </h2>
              {crewMember.job_history && crewMember.job_history.length > 0 ? (
                <JobHistoryList
                  jobIds={crewMember.job_history}
                  organizationId={currentOrganization?.id || ""}
                />
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="mt-2 text-sm text-gray-500">
                    No job history available for this crew member.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Document Modal */}
      <Modal
        isOpen={isAddDocumentModalOpen}
        onClose={() => {
          setIsAddDocumentModalOpen(false);
          setEditingDocument(null);
        }}
        title={editingDocument ? "Edit Document" : "Add Document"}
        size="md"
      >
        <CrewDocumentForm
          document={editingDocument || undefined}
          crewMemberId={crewId || ""}
          onSuccess={() => {
            setIsAddDocumentModalOpen(false);
            setEditingDocument(null);
            queryClient.invalidateQueries({ queryKey: ["crew", crewId] });
          }}
          onCancel={() => {
            setIsAddDocumentModalOpen(false);
            setEditingDocument(null);
          }}
        />
      </Modal>
    </>
  );
}
